import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, BookOpen, FileText, PlayCircle, Clock } from "lucide-react";

function toEmbed(url) {
  if (!url) return "";
  // YouTube
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export default function StudentCoursePlayer() {
  const { course_id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/student/courses/${course_id}/content`)
      .then((r) => setCourse(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load course"))
      .finally(() => setLoading(false));
  }, [course_id]);

  if (loading) {
    return <DashboardLayout><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div></DashboardLayout>;
  }
  if (!course) {
    return (
      <DashboardLayout>
        <Link to="/student/courses" className="text-sm text-brand inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Back to Courses</Link>
        <div className="mt-6 p-10 text-center text-slate-500">Course not available.</div>
      </DashboardLayout>
    );
  }

  const lessons = (course.lessons || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const lesson = lessons[activeIdx];
  const embedUrl = toEmbed(lesson?.video_url);

  return (
    <DashboardLayout>
      <Link to="/student/courses" className="text-sm text-brand inline-flex items-center gap-1.5 font-semibold hover:underline" data-testid="back-to-courses">
        <ArrowLeft size={14} /> Back to Courses
      </Link>

      <div className="mt-3 flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="heading text-2xl font-bold" data-testid="course-title">{course.title}</h1>
          <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
            <span className="text-indigo-600 font-bold">{course.level || "Beginner"}</span>
            {course.duration && <><span>·</span><Clock size={11} /><span>{course.duration}</span></>}
            {course.instructor_name && <><span>·</span><span>Instructor: {course.instructor_name}</span></>}
          </div>
        </div>
      </div>
      <p className="mt-2 text-sm text-slate-600">{course.description}</p>

      <div className="mt-6 grid lg:grid-cols-[1fr_320px] gap-5">
        {/* Main player area */}
        <div className="space-y-4">
          {lessons.length === 0 ? (
            <div className="p-12 rounded-xl border border-dashed border-slate-300 text-center text-slate-500">
              No lessons in this course yet. Check back soon.
            </div>
          ) : (
            <>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-black aspect-video" data-testid="course-video-frame">
                {embedUrl ? (
                  <iframe src={embedUrl} title={lesson.title} allowFullScreen className="w-full h-full" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
                    No video for this lesson.
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">LESSON {activeIdx + 1}</div>
                <h2 className="heading text-xl font-bold mt-1" data-testid="lesson-title">{lesson.title}</h2>
                {lesson.description && <p className="text-sm text-slate-600 mt-2">{lesson.description}</p>}

                {lesson.notes_text && (
                  <div className="mt-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 flex items-center gap-1.5">
                      <FileText size={12} /> Lesson Notes
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans" data-testid="lesson-notes-text">{lesson.notes_text}</pre>
                  </div>
                )}

                {lesson.notes_url && (
                  <a href={lesson.notes_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-full px-4 py-2 text-sm" data-testid="lesson-notes-link">
                    <FileText size={14} /> Open Notes / Resources
                  </a>
                )}
              </div>
            </>
          )}
        </div>

        {/* Lesson list sidebar */}
        <div className="rounded-xl border border-slate-200 bg-white p-3 h-fit lg:sticky lg:top-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 px-2 pt-1 pb-2 flex items-center gap-1.5">
            <BookOpen size={12} /> Course Content
          </div>
          <div className="space-y-1">
            {lessons.map((l, i) => (
              <button
                key={l.id || i}
                onClick={() => setActiveIdx(i)}
                className={`w-full text-left p-2.5 rounded-lg flex items-start gap-2 ${i === activeIdx ? "bg-indigo-50 border border-indigo-200" : "hover:bg-slate-50 border border-transparent"}`}
                data-testid={`lesson-pick-${i}`}
              >
                <PlayCircle size={16} className={`mt-0.5 ${i === activeIdx ? "text-indigo-600" : "text-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${i === activeIdx ? "text-indigo-700" : "text-slate-700"}`}>{i + 1}. {l.title || "Untitled"}</div>
                  {l.description && <div className="text-[11px] text-slate-500 line-clamp-1">{l.description}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
